import { useForm, SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import { useDispatch, useSelector } from 'app/hooks';
import { Typography, Button, Input, Select, Option } from '@material-tailwind/react';
import { FormErrors } from 'common/components/FormError';
import { useTranslation } from 'react-i18next';
import { patchTeamKeystoreIdAsync, patchUserAsync, } from 'features/users/slice';
import { isValidateErrors } from 'packages/shared/validate/types/validate';
import { ITeamPatchKeystoreIdRequest } from 'packages/shared/validate/types/team';
import { IUserRequest } from 'packages/shared/validate/types/user';
import { updateAuthentication } from '../slice';

// @todo move to shared ?
interface ISettingsUser extends Pick<IUserRequest, 'timezone'> {}

type ISettingsRequest = ITeamPatchKeystoreIdRequest & ISettingsUser;

export const SettingsForm = () => {

	const user = useSelector(state => state.authenticate);

    const { register, setValue, handleSubmit, reset, setError, formState: { errors } } = useForm<ISettingsRequest>({
		defaultValues: { 
			timezone: user.timezone,
			keystoreId: user.team.keystoreId
		}
	});

	const { onChange, ...timezoneRegistration } = register("timezone");

	const timeZones = Intl.supportedValuesOf("timeZone");

	const { t } = useTranslation();

    const dispatch = useDispatch();

	const onSubmit:SubmitHandler<ISettingsRequest> = (data, event) => {
		event?.preventDefault();

		if (data.keystoreId !== user.team.keystoreId) {
			dispatch(patchTeamKeystoreIdAsync({
				userId: user.userId,
				keystoreId: data.keystoreId
			}))
			.unwrap()
			.then((user) => {
				dispatch(updateAuthentication(user));
			})
			.catch(error => {
				if (isValidateErrors(error)) {
					error.map(e => setError(e.name as keyof ISettingsUser, {
						type: 'api',
						message: t('invalid', { ns: "form", name: t(e.name, { ns: "form" }) })
					}));

					return;
				}

				if (error?.hasOwnProperty("message") || error instanceof Error) {
					setError("root", {
						type: "api",
						message: t(error.message, { ns: "form" })
					});

					return;
				}

				setError("root", {
					type: "api",
					message: t("unknown error", { ns: "form" })
				});
			});
		}
		if (data.timezone !== user.timezone) {
			dispatch(patchUserAsync({
				userId: user.userId,
				timezone: data.timezone
			}))
			.unwrap()
			.then(user => { 
				dispatch(updateAuthentication(user));
			})
			.catch(error => {
				if (isValidateErrors(error)) {
					error.map(e => setError(e.name as keyof ISettingsUser, {
						type: 'api',
						message: t('invalid', { ns: "form", name: t(e.name, { ns: "form" }) })
					}));

					return;
				}
				
				if (error?.hasOwnProperty("message") || error instanceof Error) {
					setError("root", {
						type: "api",
						message: t(error.message, { ns: "form" })
					});

					return;
				}

				setError("root", {
					type: "api",
					message: t("unknown error", { ns: "form" })
				});
			});
		}
	};

	const onError:SubmitErrorHandler<ISettingsRequest> = (errors, event) => {
		Object.entries(errors).map(([name, error]) => {

			if (error.type === 'required') {

				setError(name as keyof ISettingsRequest, {
					type: 'required',
					message: t('required', { ns: 'form', name })
				});

				return;
			}
		});
	};

    return (
		<form autoComplete="off" className="m-auto w-80 max-w-screen-lg sm:w-96" onSubmit={handleSubmit(onSubmit, onError)}>
			<div className="mb-1 flex flex-col gap-4">
				<Typography variant="h6" color="blue-gray" className="-mb-3 text-left" placeholder={undefined} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
					{t("Keystore ID")}
				</Typography>
				<div className="relative flex w-full max-w-[24rem]">
					<Input
						onPointerEnterCapture={()=>{}} 
						onPointerLeaveCapture={()=>{}} 
						id={t("Keystore ID")}
						error={!!errors.keystoreId}
						size="lg"
						className="focus:outline-none focus:ring-0"
						label={t("Keystore ID")}
						crossOrigin={undefined}
						{...register("keystoreId")}
					/>
				</div>
			</div>
			<div className="mb-1 flex flex-col gap-4">
				<Typography variant="h6" color="blue-gray" className="-mb-3 text-left" placeholder={undefined} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
					{t("Timezone")}
				</Typography>
				<Select 
					label={t("Timezone")} 
					onPointerEnterCapture={() => { }}
					onPointerLeaveCapture={() => { }}
					placeholder={undefined}
					value={user.timezone}
					onChange={(event) => {
						setValue(timezoneRegistration.name, event!);
					}}
					{...timezoneRegistration}
				>
					{timeZones.map(timezone => (
						<Option key={`settings-timezone-${timezone}`} value={timezone}>{timezone}</Option>
					))}
                </Select>
			</div>
			
			<FormErrors errors={errors} />

			<Button className="mt-6" 
				type="submit" 
				fullWidth 
				placeholder={undefined} 
				onPointerEnterCapture={()=>{}} 
				onPointerLeaveCapture={()=>{}}
			>
				{t("Update")}
			</Button>

		</form>
	);
};
